import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle, Ticket } from "lucide-react";

// This route is registered OUTSIDE the main <App/> wrapper (so the Stripe
// redirect can reach it without a full app mount). That means the global
// axios.defaults (baseURL + Authorization) set inside <App/> never run for
// this page. We build a local axios instance here that reads the JWT from
// localStorage and points at REACT_APP_BACKEND_URL/api directly.
const buildApiClient = () => {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
  return axios.create({
    baseURL: `${backend.replace(/\/$/, "")}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

/**
 * Landing page Stripe redirects to after a season-pass purchase.
 *
 * Polls /api/season-passes/purchase-status/{session_id} every 2 seconds
 * until the backend has: (a) confirmed payment_status='paid' with Stripe,
 * (b) created the per-event bookings, and (c) fired the confirmation
 * emails. That polling call is what actually triggers the completion
 * handler in environments where the Stripe webhook isn't wired.
 */
const SeasonPassSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const [purchase, setPurchase] = useState(null);
  const [error, setError] = useState("");
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const apiRef = useRef(null);
  if (apiRef.current === null) {
    apiRef.current = buildApiClient();
  }

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      setError("Missing Stripe session id. If you were charged, contact support.");
      return;
    }

    // If the user isn't authenticated (no token), the backend will 401 the
    // polling call. Give them a clear signpost to log in and retry.
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("authToken");
    if (!hasToken) {
      setStatus("failed");
      setError(
        "You need to log in first so we can confirm your season pass. Please sign in and return to this page — your Stripe payment is safe."
      );
      return;
    }

    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const res = await apiRef.current.get(`/season-passes/purchase-status/${sessionId}`);
        if (cancelled) return;
        const data = res.data || {};
        setPurchase(data);
        if (data.payment_status === "paid" && (data.generated_booking_ids || []).length > 0) {
          setStatus("paid");
          return;
        }
        if (attemptsRef.current >= 20) {
          // Roughly 40s of polling; safe cutoff so the user isn't stuck.
          setStatus("failed");
          setError(
            "We couldn't confirm the payment within 40 seconds. If Stripe charged you, " +
              "please refresh in a minute or contact support with your session id."
          );
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus("failed");
        setError(
          e?.response?.data?.detail ||
            "Could not confirm the purchase. If Stripe charged you, contact support."
        );
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center p-6">
      <div
        className="max-w-md w-full bg-white rounded-xl shadow-lg border border-slate-200 p-6"
        data-testid="season-pass-success-card"
      >
        {status === "polling" && (
          <div className="flex flex-col items-center text-center gap-3">
            <div className="rounded-full bg-purple-100 p-3">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">Confirming your season pass…</h1>
            <p className="text-sm text-slate-600">
              We're checking with Stripe and generating your seat for every included event. This
              usually takes a couple of seconds.
            </p>
            <p className="text-xs text-slate-400" data-testid="season-pass-success-session">
              Session {sessionId?.slice(-10) || "—"}
            </p>
          </div>
        )}

        {status === "paid" && (
          <div className="flex flex-col items-center text-center gap-3">
            <div className="rounded-full bg-emerald-100 p-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1
              className="text-xl font-semibold text-slate-900"
              data-testid="season-pass-success-title"
            >
              Season pass confirmed 🎉
            </h1>
            <p className="text-sm text-slate-600">
              Your seat is booked for{" "}
              <strong data-testid="season-pass-success-event-count">
                {purchase?.generated_booking_ids?.length || 0}
              </strong>{" "}
              included event{purchase?.generated_booking_ids?.length === 1 ? "" : "s"}.
            </p>
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 w-full">
              A confirmation email with the full event list and a calendar invite (.ics) has been
              sent to your inbox.
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full mt-2">
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => navigate("/?tab=my-bookings")}
                data-testid="season-pass-success-my-bookings"
              >
                <Ticket className="h-4 w-4 mr-1" /> Go to My Bookings
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate("/")}
                data-testid="season-pass-success-home"
              >
                Back to dashboard
              </Button>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="flex flex-col items-center text-center gap-3">
            <div className="rounded-full bg-amber-100 p-3">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">We couldn't confirm the pass</h1>
            <p
              className="text-sm text-slate-600 whitespace-pre-line"
              data-testid="season-pass-success-error"
            >
              {error}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 w-full mt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
              <Link to="/" className="flex-1">
                <Button variant="outline" className="w-full">
                  Back to dashboard
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeasonPassSuccess;
